import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Box,
  Button,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Controller } from "react-hook-form";

export function InstallDialog({
  open,
  onClose,
  installForm,
  onSubmitInstall,
  wells,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Install Meter</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="install-form"
          onSubmit={installForm.handleSubmit(onSubmitInstall)}
        >
          <Controller
            name="well_ndx"
            control={installForm.control}
            render={({ field, fieldState }) => (
              <Autocomplete
                options={wells || []}
                getOptionLabel={(option) =>
                  option.well_name
                    ? `${option.well_name} (#${option.well_ndx})`
                    : `Well #${option.well_ndx}`
                }
                value={wells?.find((w) => w.well_ndx === field.value) || null}
                onChange={(_, newValue) =>
                  field.onChange(newValue?.well_ndx || null)
                }
                isOptionEqualToValue={(option, value) =>
                  option.well_ndx === value?.well_ndx
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    label="Select Well"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            )}
          />

          <Controller
            name="installed_at"
            control={installForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                label="Installation Date"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            name="installation_note"
            control={installForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                label="Notes (optional)"
                multiline
                rows={3}
                fullWidth
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          form="install-form"
          type="submit"
          variant="contained"
          color="primary"
          disabled={installForm.formState.isSubmitting}
        >
          {installForm.formState.isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            "Install"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RemoveDialog({
  open,
  onClose,
  removeForm,
  onSubmitRemove,
  selectedMeter,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove Meter from {selectedMeter?.well_name}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="remove-form"
          onSubmit={removeForm.handleSubmit(onSubmitRemove)}
        >
          <Controller
            name="removed_at"
            control={removeForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                label="Removal Date"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            name="installation_note"
            control={removeForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                label="Notes (optional)"
                multiline
                rows={3}
                fullWidth
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          form="remove-form"
          type="submit"
          variant="contained"
          color="warning"
          disabled={removeForm.formState.isSubmitting}
        >
          {removeForm.formState.isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            "Remove"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TransferDialog({
  open,
  onClose,
  transferForm,
  onSubmitTransfer,
  selectedMeter,
  wells,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transfer Meter</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="transfer-form"
          onSubmit={transferForm.handleSubmit(onSubmitTransfer)}
        >
          <TextField
            value={selectedMeter?.well_name || ""}
            margin="normal"
            label="From Well"
            fullWidth
            disabled
          />

          <Controller
            name="to_well_ndx"
            control={transferForm.control}
            render={({ field, fieldState }) => (
              <Autocomplete
                options={
                  wells?.filter(
                    (w) => w.well_ndx !== selectedMeter?.well_ndx
                  ) || []
                }
                getOptionLabel={(option) =>
                  option.well_name
                    ? `${option.well_name} (#${option.well_ndx})`
                    : `Well #${option.well_ndx}`
                }
                value={wells?.find((w) => w.well_ndx === field.value) || null}
                onChange={(_, newValue) =>
                  field.onChange(newValue?.well_ndx || null)
                }
                isOptionEqualToValue={(option, value) =>
                  option.well_ndx === value?.well_ndx
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    label="To Well"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            )}
          />

          <Controller
            name="transferred_at"
            control={transferForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                label="Transfer Date"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            name="installation_note"
            control={transferForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                label="Notes (optional)"
                multiline
                rows={3}
                fullWidth
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          form="transfer-form"
          type="submit"
          variant="contained"
          color="info"
          disabled={transferForm.formState.isSubmitting}
        >
          {transferForm.formState.isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            "Transfer"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RetireConfirmDialog({
  open,
  onClose,
  selectedMeter,
  confirmRetire,
}) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {selectedMeter?.retired ? "Confirm Reactivation" : "Confirm Retirement"}
      </DialogTitle>
      <DialogContent>
        {selectedMeter?.retired
          ? "Are you sure you want to reactivate this meter? It will become available for installation again."
          : "Are you sure you want to retire this meter? It will no longer be available for installation."}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={selectedMeter?.retired ? "success" : "error"}
          onClick={confirmRetire}
        >
          {selectedMeter?.retired ? "Reactivate" : "Retire"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
